import { useState, useEffect } from 'react';
import supabase from '../supabaseClient';
import { SB_URL, SB_KEY, sbSignedUrl } from '../services/supabase-api';
import { Inp } from './ui/FormControls';

const normalizarNj = (nj) => nj.trim().toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9_-]/g, "_");
const formatFechaHora = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const subirFoto = (file, path, onProgress) => new Promise((resolve) => {
  const xhr = new XMLHttpRequest();
  xhr.open("POST", `${SB_URL}/storage/v1/object/Documentos-registros/${path}`);
  xhr.setRequestHeader("apikey", SB_KEY);
  xhr.setRequestHeader("Authorization", `Bearer ${SB_KEY}`);
  xhr.setRequestHeader("Content-Type", file.type);
  xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
  xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300 ? `${SB_URL}/storage/v1/object/Documentos-registros/${path}` : null);
  xhr.onerror = () => resolve(null);
  xhr.send(file);
});

const buscarRegistroFotos = (almacen_id, nj) =>
  supabase.from('registros').select('*').eq('almacen_id', almacen_id).eq('tipo', 'Fotos Contenedor').eq('datos->>nj', nj).maybeSingle();

// ── OPERARIO (INTERNACIONAL): FOTOS CONTENEDOR ────────────────────
export const FotosContenedorSection = ({ currentUser, guardar }) => {
  const almacen_id = currentUser?.almacen_id;

  const [nj, setNj] = useState("");
  const [pedidoCtx, setPedidoCtx] = useState(null);
  const [salidaCtx, setSalidaCtx] = useState(null);
  const [buscandoCtx, setBuscandoCtx] = useState(false);

  const [archivos, setArchivos] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [resultado, setResultado] = useState(null);

  const [njConsulta, setNjConsulta] = useState("");
  const [consultaResultado, setConsultaResultado] = useState(undefined);
  const [consultando, setConsultando] = useState(false);

  useEffect(() => {
    if (!nj.trim() || !almacen_id) { setPedidoCtx(null); setSalidaCtx(null); return; }
    const njTrim = nj.trim();
    setBuscandoCtx(true);
    const timer = setTimeout(() => {
      Promise.all([
        supabase.from('pedidos').select('*').eq('almacen_id', almacen_id).eq('nj', njTrim).maybeSingle(),
        supabase.from('registros').select('*').eq('almacen_id', almacen_id).eq('tipo', 'Salida Contenedor').eq('datos->>nj', njTrim).maybeSingle(),
      ]).then(([{ data: pedido }, { data: salida }]) => {
        setPedidoCtx(pedido || null);
        setSalidaCtx(salida || null);
      }).finally(() => setBuscandoCtx(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [nj, almacen_id]);

  useEffect(() => {
    if (!njConsulta.trim() || !almacen_id) { setConsultaResultado(undefined); return; }
    const njTrim = njConsulta.trim();
    setConsultando(true);
    const timer = setTimeout(() => {
      buscarRegistroFotos(almacen_id, njTrim)
        .then(({ data }) => setConsultaResultado(data || null))
        .finally(() => setConsultando(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [njConsulta, almacen_id]);

  const onSeleccionArchivos = (e) => {
    const files = Array.from(e.target.files || []);
    const nuevos = files.map(f => ({ id: `${Date.now()}_${Math.random().toString(36).slice(2)}`, file: f, preview: URL.createObjectURL(f), progress: 0, estado: "pendiente" }));
    setArchivos(a => [...a, ...nuevos]);
    e.target.value = "";
  };

  const quitarArchivo = (id) => setArchivos(a => a.filter(x => x.id !== id));

  const formValido = nj.trim().length > 0 && archivos.length > 0 && !subiendo;

  const subirTodas = async () => {
    if (!formValido) return;
    setSubiendo(true);
    setResultado(null);
    const njTrim = nj.trim();
    const njNorm = normalizarNj(njTrim);
    const nuevasFotos = [];
    let fallidos = 0;

    for (let i = 0; i < archivos.length; i++) {
      const a = archivos[i];
      setArchivos(arr => arr.map(x => x.id === a.id ? { ...x, estado: "subiendo" } : x));
      const ext = a.file.name.split(".").pop();
      const path = `internacional/${njNorm}/fotos/foto_${Date.now()}_${i}.${ext}`;
      const url = await subirFoto(a.file, path, (p) => setArchivos(arr => arr.map(x => x.id === a.id ? { ...x, progress: p } : x)));
      if (url) {
        nuevasFotos.push({ url, subidoEn: new Date().toISOString() });
        setArchivos(arr => arr.map(x => x.id === a.id ? { ...x, estado: "ok", progress: 100 } : x));
      } else {
        fallidos++;
        setArchivos(arr => arr.map(x => x.id === a.id ? { ...x, estado: "error" } : x));
      }
    }

    if (nuevasFotos.length > 0) {
      const { data: existente } = await buscarRegistroFotos(almacen_id, njTrim);
      if (existente) {
        const fotosPrevias = existente.datos?.fotos || [];
        await supabase.from('registros').update({ datos: { ...existente.datos, nj: njTrim, fotos: [...fotosPrevias, ...nuevasFotos] } }).eq('id', existente.id);
      } else {
        await guardar("Fotos Contenedor", { nj: njTrim, fotos: nuevasFotos });
      }
      if (njConsulta.trim() === njTrim) {
        buscarRegistroFotos(almacen_id, njTrim).then(({ data }) => setConsultaResultado(data || null));
      }
    }

    setResultado({ ok: nuevasFotos.length, fallidos });
    setArchivos(arr => arr.filter(x => x.estado !== "ok"));
    setSubiendo(false);
  };

  const progresoGlobal = archivos.length > 0 ? Math.round(archivos.reduce((s, a) => s + (a.estado === "ok" ? 100 : a.progress), 0) / archivos.length) : 0;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <h2 className="font-bold text-gray-800 border-b pb-2">📸 Fotos Contenedor</h2>
        <Inp label="Nº de documento NJ *" value={nj} onChange={setNj} placeholder="NJ 54832453" />

        {buscandoCtx && <p className="text-xs text-gray-400">Buscando...</p>}
        {!buscandoCtx && nj.trim() && (pedidoCtx || salidaCtx) && (
          <div className="space-y-1">
            {pedidoCtx && (
              <p className="text-sm text-green-700 font-semibold bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                ✅ Pedido {pedidoCtx.numero_pedido} — {pedidoCtx.cliente || "sin cliente"}
              </p>
            )}
            {salidaCtx && (
              <p className="text-sm text-green-700 font-semibold bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                ✅ Salida registrada el {salidaCtx.fecha}
              </p>
            )}
          </div>
        )}
        {!buscandoCtx && nj.trim() && !pedidoCtx && !salidaCtx && (
          <p className="text-sm text-amber-700 font-semibold bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            ⚠️ No hay pedido ni salida con este NJ. Puedes subir las fotos igualmente.
          </p>
        )}

        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase">Fotos</label>
          <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:bg-gray-50 transition-all">
            <span className="text-3xl mb-2">📷</span>
            <span className="text-sm text-gray-500 font-semibold">Toca para hacer fotos o elegir de la galería</span>
            <span className="text-xs text-gray-400 mt-1">Selección múltiple — JPG, PNG</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={onSeleccionArchivos} />
          </label>
        </div>

        {archivos.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {archivos.map(a => (
              <div key={a.id} className="relative border rounded-lg overflow-hidden">
                <img src={a.preview} alt="preview" className="w-full h-24 object-cover" />
                {a.estado === "pendiente" && (
                  <button onClick={() => quitarArchivo(a.id)} className="absolute top-1 right-1 bg-black/60 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">✕</button>
                )}
                {a.estado === "subiendo" && (
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5">
                    <div className="h-1 bg-gray-500 rounded-full overflow-hidden"><div className="h-1 bg-cyan-400" style={{ width: `${a.progress}%` }} /></div>
                  </div>
                )}
                {a.estado === "ok" && <span className="absolute top-1 right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">✓</span>}
                {a.estado === "error" && <span className="absolute top-1 right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">✕</span>}
              </div>
            ))}
          </div>
        )}

        {subiendo && (
          <div>
            <p className="text-xs text-gray-500 mb-1">Subiendo... {progresoGlobal}%</p>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-2 bg-cyan-600 transition-all" style={{ width: `${progresoGlobal}%` }} /></div>
          </div>
        )}

        {resultado && (
          <p className={`text-sm font-semibold rounded-lg px-3 py-2 ${resultado.fallidos > 0 ? "text-amber-700 bg-amber-50 border border-amber-200" : "text-green-700 bg-green-50 border border-green-200"}`}>
            {resultado.fallidos > 0
              ? `⚠️ ${resultado.ok} foto(s) subida(s), ${resultado.fallidos} fallida(s) — puedes reintentar`
              : `✅ ${resultado.ok} foto(s) subida(s) correctamente`}
          </p>
        )}

        <button
          onClick={subirTodas}
          disabled={!formValido}
          className="w-full bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all"
        >
          {subiendo ? "⏳ Subiendo..." : `Subir ${archivos.length || ""} foto${archivos.length === 1 ? "" : "s"} →`}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <h2 className="font-bold text-gray-800 border-b pb-2">🔍 Consultar fotos por NJ</h2>
        <Inp label="Nº de documento NJ" value={njConsulta} onChange={setNjConsulta} placeholder="NJ 54832453" />
        {consultando && <p className="text-xs text-gray-400">Buscando...</p>}
        {!consultando && consultaResultado === null && (
          <p className="text-sm text-gray-500">No hay fotos registradas para este NJ.</p>
        )}
        {!consultando && consultaResultado && (
          <div className="grid grid-cols-3 gap-2">
            {(consultaResultado.datos?.fotos || []).map((f, i) => (
              <a
                key={i}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="block border rounded-lg overflow-hidden"
                onClick={async (e) => {
                  e.preventDefault();
                  const url = await sbSignedUrl(f.url);
                  window.open(url || f.url, "_blank");
                }}
              >
                <img
                  src={f.url}
                  alt={`foto ${i + 1}`}
                  className="w-full h-24 object-cover"
                  onError={async (e) => {
                    const url = await sbSignedUrl(f.url);
                    if (url) e.target.src = url;
                  }}
                />
                <p className="text-[10px] text-gray-500 text-center py-0.5">{formatFechaHora(f.subidoEn)}</p>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
